import {
  type InvoiceListItemDto,
  type ServiceTypeEnum,
  type Statement,
  StatementStatusEnum,
} from '@sr-sdks/api-sdk-axios';
import { paidInvoicesParams } from '../../../api/payments/invoiceApi';
import {
  type StatementsQueryParams,
  fetchStatements,
} from '../../../api/payments/statementApi';
import { serviceBadgeLabel } from '../../../components/Badges/appBadgeConstants';
import {
  type StatementsCsvParams,
  fetchStatementsCsvPage,
} from '../../../components/statementsCsvSpec';
import type { RoyaltiesRegion } from '../../../layout/royaltiesLayoutConfig';
import { csvRequestConfig } from '../../../utils/csv/csvExport';
import { csvColumn, csvDate } from '../../../utils/csv/toCsvChunk';
import { ensureStableSort } from '../../../utils/helpers/sort';

/**
 * A royalties table's filters: everything it sends but page and pageSize. The
 * list and its CSV export both take this one object.
 */
export type RoyaltiesListFilters = {
  searchTerm: string;
  serviceType: ServiceTypeEnum;
  sortBy: string;
  workspaceId: string;
};

export const ROYALTIES_EXPORT_PAGE = {
  anz: {
    earnings: 'royalties-anz-earnings-statements',
    title: 'royalties-anz-title-statements',
  },
  world: {
    earnings: 'royalties-world-earnings-statements',
    title: 'royalties-world-title-statements',
  },
} as const satisfies Record<
  RoyaltiesRegion,
  Record<'earnings' | 'title', string>
>;

const col = csvColumn;

/** The Title statements table's columns (APP-2510 D15). */
export const titleStatementsCsvColumns = [
  col<Statement>('date', 'Date', (row) => csvDate(row.statementDate)),
  col<Statement>(
    'titleStatementId',
    'Title statement ID',
    (row) => row.statementNo,
  ),
  col<Statement>('title', 'Title', (row) => row.beneficiaryTitleName),
  col<Statement>('season', 'Season', (row) => row.beneficiarySeasonNo),
  col<Statement>('service', 'Service', (row) =>
    serviceBadgeLabel(row.serviceType),
  ),
  col<Statement>('client', 'Client', (row) => row.beneficiaryClientName, true),
  col<Statement>('amount', 'Amount', (row) => row.amountNet),
  col<Statement>('currency', 'Currency', (row) => row.currency),
];

/** The Earnings statements table's columns; no prototype, so the table decides. */
export const earningsStatementsCsvColumns = [
  col<InvoiceListItemDto>('date', 'Date', (row) => csvDate(row.invoiceDate)),
  col<InvoiceListItemDto>(
    'earningsStatementId',
    'Earnings statement ID',
    (row) => row.invoiceNo,
  ),
  col<InvoiceListItemDto>(
    'payoutReferenceId',
    'Payout reference ID',
    (row) => row.payoutRefNo,
  ),
  col<InvoiceListItemDto>('service', 'Service', (row) =>
    serviceBadgeLabel(row.serviceType),
  ),
  col<InvoiceListItemDto>('amount', 'Amount', (row) => row.amountNet),
  col<InvoiceListItemDto>('currency', 'Currency', (row) => row.currency),
];

/**
 * One page of a royalties table's query. The list hooks and the CSV fetchers
 * both build from these, so an export can't drift from the table it mirrors.
 */
export const titleStatementsQueryParams = (
  { searchTerm, serviceType, sortBy, workspaceId }: RoyaltiesListFilters,
  page: number,
  pageSize: number,
) =>
  ({
    beneficiaryWorkspaceId: workspaceId,
    page,
    pageSize,
    searchTerm,
    serviceType: [serviceType],
    sortBy: ensureStableSort(sortBy),
    status: StatementStatusEnum.Finalised,
  }) satisfies StatementsQueryParams;

const earningsStatementsFilters = ({
  searchTerm,
  serviceType,
  sortBy,
  workspaceId,
}: RoyaltiesListFilters) =>
  ({
    beneficiaryWorkspaceId: workspaceId,
    searchTerm,
    serviceType: [serviceType],
    sortBy,
  }) satisfies StatementsCsvParams;

export const earningsStatementsQueryParams = (
  filters: RoyaltiesListFilters,
  page: number,
  pageSize: number,
) =>
  paidInvoicesParams({
    ...earningsStatementsFilters(filters),
    page,
    pageSize,
  });

export const fetchTitleStatementsCsvPage = (
  params: RoyaltiesListFilters,
  page: number,
  pageSize: number,
  signal: AbortSignal,
) =>
  fetchStatements(
    titleStatementsQueryParams(params, page, pageSize),
    csvRequestConfig(signal),
  );

/** The Disbursements/Residuals export's fetcher, over this table's filters. */
export const fetchEarningsStatementsCsvPage = (
  params: RoyaltiesListFilters,
  page: number,
  pageSize: number,
  signal: AbortSignal,
) =>
  fetchStatementsCsvPage(
    earningsStatementsFilters(params),
    page,
    pageSize,
    signal,
  );
